<template>
  <div class="agent-list-container">
    <box>
      <h2 class="title">
        {{ $t('forwarders.agents') }}
        <div class="level">
          <div class="level-left subtitle">
            {{ $t('forwarders.agentsSubtitle') }}
          </div>
        </div>
      </h2>
      <data-table
        ref="dataTable"
        :data="agents"
        :columns="columns"
        :loading="loading"
        :searchable="true"
        :items-per-page-options="[10, 15, 30, 50]"
        :current-page="currentPage"
        :search-value="currentSearch"
        :default-items-per-page="currentPageSize"
        :default-sort-by="currentSort.by"
        :default-sort-order="currentSort.order"
        :striped="true"
        :hoverable="true"
        :server-side="true"
        :total-items="totalItems"
        @search="handleSearch"
        @sort="handleSort"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      >
        <template #cell-stt="{ index }">
          <span>{{ index + 1 }}</span>
        </template>

        <template #cell-agent_hostname="{ value }">
          {{ value || $t('notAvailable') }}
        </template>

        <template #cell-agent_mac="{ value }">
          {{ value || $t('notAvailable') }}
        </template>

        <template #cell-token="{ value }">
          <div class="token-cell">
            {{ value || $t('notAvailable') }}
          </div>
        </template>

        <template #cell-agent_version="{ value }">
          {{ value || $t('notAvailable') }}
        </template>

        <template #cell-agent_status="{ value }">
          <div v-if="value === 'online'" class="tag is-success is-light">
            {{ $t('online') }}
          </div>
          <div v-else-if="value === 'offline'" class="tag is-danger is-light">
            {{ $t('offline') }}
          </div>
          <span v-else class="has-text-grey">{{ $t('notAvailable') }}</span>
        </template>

        <template #cell-ips="{ value }">
          <div v-if="value && value.length > 0" class="tags">
            <span v-for="ip in value.map((obj) => obj.ip)" :key="ip" class="tag is-success is-light">
              {{ ip }}
            </span>
          </div>
          <span v-else class="has-text-grey">{{ $t('forwarders.noIPs') }}</span>
        </template>

        <template #cell-agent_last_seen="{ value }">
          {{ value ? formatDate(value) : $t('none') }}
        </template>

        <template #cell-actions="{ row }">
          <button
            class="button is-text"
            :disabled="!canDeleteAgent(row)"
            :class="{ 'is-loading': isDeleting(row.id) }"
            @click.prevent="handleDelete(row)"
          >
            <span>{{ $t('delete') }}</span>
          </button>
        </template>
      </data-table>
    </box>

    <confirmation-dialog
      :show="showDeleteConfirm"
      :title="$t('delete')"
      :message="deleteConfirmMessage"
      :warning-text="$t('forwarders.deleteAgentWarning')"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import DataTable from '@/components/common/DataTable.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import { format } from 'date-fns'

export default {
  name: 'AgentList',
  components: {
    DataTable,
    ConfirmationDialog
  },
  data() {
    return {
      agents: [],
      loading: false,
      totalItems: 0,
      currentPage: 1,
      currentPageSize: 10,
      currentSort: {
        by: 'agent_last_seen',
        order: 'desc'
      },
      currentSearch: '',
      deletingAgentIds: [],
      showDeleteConfirm: false,
      pendingDeleteItem: null
    }
  },
  computed: {
    deleteConfirmMessage() {
      if (!this.pendingDeleteItem) return this.$t('rus')
      const name = this.pendingDeleteItem.agent_hostname || this.pendingDeleteItem.token || this.$t('notAvailable')
      return this.$t('forwarders.deleteAgentConfirm', { name })
    },
    columns() {
      return [
        {
          key: 'stt',
          label: this.$t('stt'),
          width: '60px',
          sortable: false,
          align: 'center'
        },
        {
          key: 'agent_hostname',
          field: 'agent_hostname',
          label: this.$t('forwarders.hostname'),
          sortable: true,
          width: '15%'
        },
        {
          key: 'agent_mac',
          field: 'agent_mac',
          label: this.$t('forwarders.macAddress'),
          sortable: true
        },
        {
          key: 'token',
          field: 'token',
          label: this.$t('token'),
          sortable: true
        },
        {
          key: 'agent_version',
          field: 'agent_version',
          label: this.$t('version'),
          width: '10%',
          sortable: true
        },
        {
          key: 'agent_status',
          field: 'agent_status',
          label: this.$t('status.status'),
          width: '10%',
          sortable: true,
          align: 'center'
        },
        {
          key: 'ips',
          field: 'ips',
          label: this.$t('forwarders.ips'),
          sortable: false
        },
        {
          key: 'agent_last_seen',
          field: 'agent_last_seen',
          label: this.$t('forwarders.lastSeen'),
          width: '20%',
          sortable: true
        },
        {
          key: 'actions',
          label: this.$t('actions'),
          sortable: false,
          width: '120px',
          align: 'center'
        }
      ]
    }
  },
  mounted() {
    this.loadAgents()
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return this.$t('none')
      try {
        return format(new Date(dateString), 'dd/MM/yyyy HH:mm:ss')
      } catch (error) {
        console.error('Error formatting agent date:', error)
        return dateString
      }
    },
    async loadAgents() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.currentPageSize,
          sort: this.currentSort.by,
          order: this.currentSort.order === 'asc' ? 'asc' : 'desc'
        }

        if (this.currentSearch) {
          params.filter = this.currentSearch
        }

        const response = await this.$http.get('/api/v1/agents', { params })

        if (response && response.data && response.data.data) {
          const result = response.data.data
          this.agents = Array.isArray(result.items) ? result.items : []
          this.totalItems = typeof result.total === 'number' ? result.total : 0
        } else {
          this.agents = []
          this.totalItems = 0
        }
      } catch (error) {
        console.error('Error loading agents:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('forwarders.loadError') || 'Failed to load agents'
        })
      } finally {
        this.loading = false
      }
    },
    handleSearch(searchQuery) {
      this.currentSearch = searchQuery
      this.currentPage = 1
      this.loadAgents()
    },
    handleSort(sortConfig) {
      this.currentSort = {
        by: sortConfig.by || 'agent_last_seen',
        order: sortConfig.order || 'desc'
      }
      this.currentPage = 1
      this.loadAgents()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadAgents()
    },
    handlePageSizeChange(pageSize) {
      this.currentPageSize = pageSize
      this.currentPage = 1
      this.loadAgents()
    },
    isDeleting(agentId) {
      return !!agentId && this.deletingAgentIds.includes(agentId)
    },
    canDeleteAgent(item) {
      return !!item?.id && !this.isDeleting(item.id)
    },
    async handleDelete(item) {
      if (!this.canDeleteAgent(item)) return
      this.pendingDeleteItem = item
      this.showDeleteConfirm = true
    },
    cancelDelete() {
      this.showDeleteConfirm = false
      this.pendingDeleteItem = null
    },
    async confirmDelete() {
      const item = this.pendingDeleteItem
      this.showDeleteConfirm = false
      this.pendingDeleteItem = null
      if (!item) return

      this.deletingAgentIds = [...this.deletingAgentIds, item.id]

      try {
        const response = await this.$http.delete(`/api/v1/agents/${item.id}`)
        if (response && response.data) {
          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: response.data.message || this.$t('deleteSuccess')
          })

          const shouldMovePreviousPage = this.agents.length === 1 && this.currentPage > 1
          if (shouldMovePreviousPage) {
            this.currentPage -= 1
          }

          await this.loadAgents()
        }
      } catch (error) {
        console.error('Error deleting agent:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('deleteError')
        })
      } finally {
        this.deletingAgentIds = this.deletingAgentIds.filter(id => id !== item.id)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.agent-list-container {
  width: 100%;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;

  .tag {
    margin: 0;
  }
}

.token-cell {
  white-space: normal;
  word-break: break-all;
  overflow-wrap: anywhere;
}
</style>
